import { LocaleDetails, supportedLocales } from '../types/languages.type';

/**
 * Which locale a conversation turn should be RENDERED in.
 *
 * The conversation model reports the language the shopper's own words are
 * written in (`langCode`), or "und" when the message is nothing but brand
 * names, model numbers and units — text a speaker of any language types
 * identically. That neutrality call is what the UI actually needs, and the
 * model is the only thing in the pipeline competent to make it on three words.
 *
 * WHY NOT the lexical detector below (measured, 2026-09-20): it counts FUNCTION
 * words, so a query made purely of content words carries no vote —
 * `detectSiblingLocaleForQuery('aspirateur automatique silencieux', en-CA)`
 * returns null. Gating the UI flip on it meant a French shopper on the English
 * site got a French conversation inside an entirely English interface, and was
 * sent to the English page while the French one already existed. Product
 * queries are mostly content words, so that missed the common case.
 *
 * The guard it replaces existed for a real reason (measured 2026-08-10): a bare
 * `langCode` sent an fr-CA shopper typing "iPhone 17 pro max" to
 * /en-CA/iphone-17-pro-max. That case is now answered at its source — the model
 * emits "und" for a bare product name — instead of by demanding French
 * function words as corroboration.
 *
 * Fails closed by construction. "und", an empty string, junk, and a language
 * this region does not serve all match nothing reachable and return the locale
 * unchanged, so the only way to move is a real language of this very region.
 * Neutral follow-ups therefore keep a conversation where it already is rather
 * than snapping it back to the site language. A locale PATH arriving where a
 * language code belongs (/api/page-question emits `langCode: "en-CA"`) builds
 * "en-CA-CA" and matches nothing, which is exactly what it did before.
 *
 * `homeLocale` is the locale the page was rendered with, and it anchors what is
 * reachable: the siblings of HOME, plus home itself. Anchoring on the active
 * locale instead would trap minority-language shoppers — supportedLocales
 * ['gl-ES'] is [es-ES, en-ES], so a Galician who flipped to es-ES would find no
 * gl-ES sibling to walk back to. Keeping the allow-list to one region also
 * means a flip can never cross a country or reach a locale with no shelf.
 */
export const resolveTurnLocale = (
  langCode: string | undefined | null,
  activeLocale: LocaleDetails,
  homeLocale: LocaleDetails = activeLocale,
): LocaleDetails => {
  if (typeof langCode !== 'string') return activeLocale;
  const code = langCode.trim().toLowerCase();
  if (!code) return activeLocale;
  // Matched by PATH, never by `languageCode`: most locales carry a bare "fr",
  // but pt-BR and pt-PT carry "pt-BR"/"pt-PT" as their language code, so a
  // languageCode comparison would silently never flip a Portuguese shopper.
  const targetPath = `${code}-${homeLocale.regionCode}`;
  if (targetPath === activeLocale.path) return activeLocale;
  const reachable = [homeLocale, ...(supportedLocales[homeLocale.path] ?? [])];
  return reachable.find((candidate) => candidate.path === targetPath) ?? activeLocale;
};


/**
 * Detect that a query is written in a SIBLING locale's language, so retrieval
 * can run against the slice whose dictionary actually understands it.
 *
 * WHY THIS EXISTS (measured, 2026-08-10): an en-CA shopper typed the French
 * query "veste de sauvetage". search_url_mappings_v2 tokenised it with the
 * ENGLISH dictionary, where "de" is not a stopword — so "de" and "sauvetage"
 * became content tokens with near-zero document frequency in the en-CA slice,
 * i.e. the HIGHEST IDF weights in the query. French rows (retrieval is
 * region-wide) match those tokens through their 'simple'-config vectors, so ten
 * unrelated French pages cleared the 0.70 display gate at coverage 0.724 —
 * several with additive scores as low as -9700. The same query searched as
 * fr-CA tops out at coverage 0.578 and is correctly declined.
 *
 * The fix is NOT translation: it is picking the (region, lang) slice whose
 * dictionary and corpus statistics can judge the query honestly. Rows are
 * retrieved region-wide either way, every row carries the full per-locale href
 * map, and the app already prefers the active locale's row/URL when rendering —
 * so a French query on the English site organically surfaces the concept page
 * with the English URL when a sibling exists, and correctly declines when it
 * does not.
 *
 * DETECTION is deliberately dumb and lexical: count language-marker FUNCTION
 * words (articles, prepositions, first-person openers) per region-sibling
 * language. Function words are what poison the IDF math, so they are exactly
 * the evidence worth trusting. One sibling marker with ZERO active-language
 * markers flips the slice; any active-language marker keeps the query home.
 * Content words carry no vote — "aspirateur robot" on en-CA stays on the
 * English slice, where region-wide retrieval already finds the French page
 * organically with honest coverage.
 *
 * False positives (an English query containing "de": "eau de parfum") are
 * benign by construction: retrieval stays region-wide, English rows still
 * match their own tokens, and locale-preferring dedup keeps the English row.
 */
const MARKER_WORDS: Record<string, ReadonlySet<string>> = {
  en: new Set(['the', 'for', 'with', 'and', 'this', 'that', 'my', 'i', 'want', 'an', 'of', 'to']),
  fr: new Set([
    'le', 'la', 'les', 'de', 'du', 'des', 'un', 'une', 'pour', 'avec', 'sans',
    'et', 'sur', 'dans', 'je', 'veux', 'cherche', 'mon', 'ma', 'mes', 'qui', 'que',
  ]),
  es: new Set([
    'el', 'los', 'las', 'de', 'del', 'para', 'con', 'sin', 'una', 'uno', 'unos',
    'y', 'quiero', 'busco', 'mi', 'mis', 'que',
  ]),
  de: new Set([
    'der', 'die', 'das', 'für', 'mit', 'ohne', 'ein', 'eine', 'einen', 'und',
    'ich', 'möchte', 'suche', 'zum', 'zur', 'auf',
  ]),
  it: new Set([
    'il', 'lo', 'gli', 'per', 'con', 'senza', 'una', 'uno', 'di', 'del', 'della',
    'e', 'voglio', 'cerco', 'che',
  ]),
  nl: new Set([
    'de', 'het', 'een', 'voor', 'met', 'zonder', 'en', 'ik', 'wil', 'zoek',
    'van', 'op',
  ]),
  pt: new Set([
    'o', 'os', 'as', 'para', 'com', 'sem', 'um', 'uma', 'de', 'do', 'da',
    'e', 'quero', 'procuro', 'que',
  ]),
  pl: new Set([
    'dla', 'z', 'bez', 'i', 'do', 'na', 'chcę', 'szukam', 'który', 'która',
  ]),
  sv: new Set([
    'för', 'med', 'utan', 'och', 'en', 'ett', 'jag', 'vill', 'söker', 'på',
  ]),
};

/** Kana is unambiguously Japanese; we serve no other CJK language, so shared
 *  ideographs are treated as Japanese wherever a ja sibling exists. */
const JAPANESE_CHARS = /[぀-ヿ一-鿿]/;

const markerHits = (tokens: string[], languageCode: string): number => {
  const markers = MARKER_WORDS[languageCode];
  if (!markers) return 0;
  let hits = 0;
  for (const token of tokens) if (markers.has(token)) hits++;
  return hits;
};

/**
 * Returns the region-sibling locale whose language the query is written in, or
 * null when the query reads as the active language (or nothing can be told).
 * Pure and cheap — a lowercase split and a few Set lookups.
 */
export const detectSiblingLocaleForQuery = (
  searchTerm: string,
  locale: LocaleDetails,
): LocaleDetails | null => {
  if (!searchTerm?.trim()) return null;
  const siblings = (supportedLocales[locale.path] ?? []).filter(
    (sibling) => sibling.languageCode !== locale.languageCode,
  );
  if (siblings.length === 0) return null;

  if (JAPANESE_CHARS.test(searchTerm)) {
    const ja = siblings.find((sibling) => sibling.languageCode === 'ja');
    if (ja && locale.languageCode !== 'ja') return ja;
  }

  const tokens = searchTerm
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);
  // A one-word query carries no function words by definition — nothing to read.
  if (tokens.length < 2) return null;
  // Any active-language marker means the query plausibly IS the active
  // language; mixed-language queries stay home (region-wide retrieval still
  // gives their foreign tokens a chance to match).
  if (markerHits(tokens, locale.languageCode) > 0) return null;

  let best: LocaleDetails | null = null;
  let bestHits = 0;
  for (const sibling of siblings) {
    const hits = markerHits(tokens, sibling.languageCode);
    if (hits > bestHits) {
      best = sibling;
      bestHits = hits;
    }
  }
  return best;
};
